import { createNgModule, EnvironmentInjector, inject, Injectable, Type } from '@angular/core';
import { CanActivateFn, PRIMARY_OUTLET, Route, Router, ROUTES, Routes } from '@angular/router';
import { buildRoutePath } from '@services/guards/get-full-route-path';
import { Guard } from '@services/guards/guard';
import { GUARDS, GuardType } from '@services/guards/guards';

type LazyRoute = Route & { _loadedRoutes?: Routes };
type RouteMatch = {
  route?: Route;
  params: Record<string, string>;
  path?: string;
};

@Injectable({ providedIn: 'root' })
export class GuardsService {
  private readonly guards: Record<GuardType, Guard> = {
    Auth: inject(GUARDS.Auth),
    Pharmacy: inject(GUARDS.Pharmacy),
    Scorecard: inject(GUARDS.Scorecard),
    Inventory: inject(GUARDS.Inventory),
    Workflow: inject(GUARDS.Workflow),
  };
  private readonly injector = inject(EnvironmentInjector);
  private readonly router = inject(Router);

  async canShow(url: string) {
    const { route, params, path } = await this.getFinalRoute(url);
    const guard = this.getGuard(route);
    if (!guard) throw new Error(`No guard for route: ${route}`);
    return await guard.canShow(path ?? url, params);
  }

  async getFinalRoute(url: string): Promise<RouteMatch> {
    const tree = this.router.parseUrl(url);
    const segments = tree.root.children[PRIMARY_OUTLET]?.segments.map(segment => segment.path) ?? [];
    return (await this.matchRoute(this.router.config, segments)) ?? { params: {} };
  }

  private getGuard(route?: Route): Guard {
    if (route?.canActivate?.length) {
      const firstGuard = route.canActivate[0] as CanActivateFn;
      for (const guard of Object.values(this.guards)) {
        if (firstGuard.name === guard.constructor.name) return guard;
      }
      throw new Error('Unsupported guard');
    } else {
      return this.guards['Auth'];
    }
  }

  private async matchRoute(
    routes: Routes | undefined,
    segments: string[],
    parentSegments: string[] = [''],
    parent?: Route,
  ): Promise<RouteMatch | undefined> {
    if (!routes) return undefined;

    for (const route of routes) {
      const path = route.path ?? '';

      if (path === '**') continue;

      const nextSegments = [...parentSegments, path];

      if (path === '') {
        if (route.redirectTo !== undefined) continue;
        if (route.pathMatch === 'full' && segments.length > 0) continue;

        const nested = await this.matchRoute(await this.getChildren(route), segments, nextSegments, route);
        if (nested) return nested;
        if (segments.length === 0) return { route, params: {}, path: buildRoutePath(nextSegments) };
        continue;
      }

      const routeSegments = path.split('/');
      if (routeSegments.length > segments.length) continue;

      const params: Record<string, string> = {};
      const isMatch = routeSegments.every((seg, index) => {
        if (seg.startsWith(':')) {
          params[seg.slice(1)] = segments[index];
          return true;
        }
        return seg === segments[index];
      });
      if (!isMatch) continue;

      const rest = segments.slice(routeSegments.length);
      const nested = await this.matchRoute(await this.getChildren(route), rest, nextSegments, route);
      if (nested) return { route: nested.route, params: { ...params, ...nested.params }, path: nested.path };

      if (rest.length === 0 && route.redirectTo === undefined) return { route, params, path: buildRoutePath(nextSegments) };
    }

    if (segments.length === 0 && parent) return { route: parent, params: {}, path: buildRoutePath(parentSegments) };
    return undefined;
  }

  private async getChildren(route: LazyRoute): Promise<Routes | undefined> {
    if (route.children) return route.children;
    if (route._loadedRoutes) return route._loadedRoutes;
    if (!route.loadChildren) return undefined;

    try {
      const loaded = await (route.loadChildren as () => Promise<Type<unknown> | Routes>)();
      const routes = Array.isArray(loaded) ? loaded : this.extractModuleRoutes(loaded);
      route._loadedRoutes = routes;
      return routes;
    } catch {
      return undefined;
    }
  }

  private extractModuleRoutes(moduleType: Type<unknown>): Routes {
    const moduleRef = createNgModule(moduleType, this.injector);
    const configs = moduleRef.injector.get(ROUTES, [] as Route[][], { optional: true }) ?? [];
    return configs.flat();
  }
}
